import type { NavigationGuardNext, NavigationGuardWithThis, RouteLocationNormalized } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useAccountStore } from '@core/stores/account.store';
import { LOGIN } from '@apps/account/routing';
import { availableFeaturesFromStudioId } from '@shared/services/features.service';
import { useAppInsightsLogger } from './plugins/appInsights';
import { useEventBus } from '@vueuse/core';
import { verificationRequiredKey } from '@shared/events';
import { identifier } from './models/base';

const accountVerificationBus = useEventBus(verificationRequiredKey);

const permissionRouteGuard = (permissions: string[]): NavigationGuardWithThis<undefined> =>
  (async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
    const accountStore = useAccountStore();
    await accountStore.initialize();
    const appInsightsLogger = useAppInsightsLogger();
    appInsightsLogger?.authenticate(accountStore.userId);
    if (!accountStore.loggedIn) {
      next({ name: LOGIN, query: { ret: to.fullPath } });
      return;
    }

    // Check if user has required permissions
    if (!permissions.some((p) => accountStore.permissions.includes(p))) {
      // Redirect to home or another page
      next({ name: 'studioadmin-home', params: { studioId: to.params.studioId } });
      return;
    }
    next();
  }).bind(undefined);

const { error } = useToast();
const featureFlagRouteGuard = (featureFlag: string, routeIfFail: string): NavigationGuardWithThis<undefined> =>
  (async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
    const { studioId, jobId } = to.params;
    const featureIdentifiers = (await availableFeaturesFromStudioId(+studioId)).map((x) => x.identifier);
    if (!featureIdentifiers.includes(featureFlag)) {
      next({ name: routeIfFail, params: { studioId: studioId, jobId: jobId } });
      error('Access to Route Denied');
    }
    next();
  }).bind(undefined);

const studioVerificationRouteGuard = (routeIfFail: string, homeRouteName: string): NavigationGuardWithThis<undefined> =>
  (async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
    const { useFeaturesStore } = await import('@shared/stores/features.store');
    const featuresStore = useFeaturesStore();

    if (!featuresStore.initialized && to.params.studioId) {
      await featuresStore.initializeWithStudioId(to.params.studioId as unknown as identifier);
    }

    if (!featuresStore.hasFeature('studioVerification')) {
      next();
      return;
    }

    if (to.name === homeRouteName) {
      next();
      return;
    }

    const { useStudioAdminStudioStore } = await import('@apps/studioadmin/stores/studioAdminStudio.store');
    const studioStore = useStudioAdminStudioStore();
    const { studioId } = to.params;

    // Ensure studio details are loaded before checking verification
    await studioStore.initialize(studioId as unknown as identifier);
    const studioDetails = studioStore.studioDetails;
    if (!studioDetails?.isMillersVerified) {
      error("Your studio needs to be verified by Miller's to access this feature");
      next({ name: routeIfFail, params: { studioId: studioId } });
      accountVerificationBus.emit();
      return;
    }

    next();
  }).bind(undefined);

export { permissionRouteGuard, featureFlagRouteGuard, studioVerificationRouteGuard };
